import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";
import { AnalysisModulesGrid } from "@/components/subpages/AnalysisModulesGrid";
import { FaqList } from "@/components/subpages/FaqList";
import analyseHero from "@/assets/analyse-hero.jpg.asset.json";

export const Route = createFileRoute("/was-analysieren-wir")({
  head: () => ({
    meta: [
      { title: "Was analysiert Energy Prime 360? – 12 Bereiche im Detail" },
      {
        name: "description",
        content:
          "Energy Prime analysiert Gebäude, PV, Speicher, Wärmepumpe, Fördermittel, Finanzierung, Steuer, Marktpreise, Wirtschaftlichkeit 10/20/30 Jahre und Risiken – alles im Zusammenspiel.",
      },
      { property: "og:title", content: "Was analysieren wir für Sie?" },
      { property: "og:description", content: "Technik, Wirtschaftlichkeit, Förderung, Steuer – alle Faktoren im Blick." },
      { property: "og:url", content: "https://www.energie-prime.de/was-analysieren-wir" },
    ],
    links: [{ rel: "canonical", href: "https://www.energie-prime.de/was-analysieren-wir" }],
  }),
  component: Page,
});


function Check({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm text-navy/85">
      <span className="inline-flex w-5 h-5 rounded-full bg-[color-mix(in_oklab,var(--leaf)_15%,white)] items-center justify-center shrink-0">
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-[var(--leaf-deep)]" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
      <span>{children}</span>
    </div>
  );
}

type Feature = { title: string; body: string; color: string; icon: React.ReactNode };

const features: Feature[] = [
  {
    title: "Technik, Wirtschaftlichkeit & Energie",
    body: "Wir analysieren technische Konzepte, Energieflüsse und Effizienzpotenziale – und bewerten Investitionen, Betriebskosten und Einsparungen realistisch.",
    color: "bg-navy",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M19.43 12.98a7.97 7.97 0 000-1.96l2.04-1.58a.5.5 0 00.12-.64l-1.93-3.34a.5.5 0 00-.61-.22l-2.4.96a8 8 0 00-1.7-.98l-.36-2.55A.5.5 0 0014.1 2h-3.86a.5.5 0 00-.5.42l-.36 2.55a8 8 0 00-1.7.98l-2.4-.96a.5.5 0 00-.61.22L2.74 8.55a.5.5 0 00.12.64l2.04 1.58a8 8 0 000 2.46l-2.04 1.58a.5.5 0 00-.12.64l1.93 3.34a.5.5 0 00.61.22l2.4-.96a8 8 0 001.7.98l.36 2.55a.5.5 0 00.5.42h3.86a.5.5 0 00.5-.42l.36-2.55a8 8 0 001.7-.98l2.4.96a.5.5 0 00.61-.22l1.93-3.34a.5.5 0 00-.12-.64l-2.04-1.58zM12 16a4 4 0 110-8 4 4 0 010 8z" />
      </svg>
    ),
  },
  {
    title: "Fördermittel, Finanzierung & Steuern",
    body: "Wir identifizieren optimale Förderungen, prüfen Finanzierungsoptionen und analysieren steuerliche Vorteile für Ihre individuelle Situation.",
    color: "bg-[var(--leaf-deep)]",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="1.6" fill="none" />
        <path d="M4 9v7l8 4 8-4V9" fill="currentColor" opacity="0.85" />
      </svg>
    ),
  },
  {
    title: "Risiken, Potenziale & Zukunftsperspektiven",
    body: "Wir bewerten Risiken, erkennen Potenziale und berücksichtigen technologische Entwicklungen sowie gesetzliche Trends für eine zukunftssichere Entscheidung.",
    color: "bg-orange",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v5h2v-5z" />
      </svg>
    ),
  },
  {
    title: "Alles im Zusammenspiel analysiert",
    body: "Alle Faktoren werden integriert betrachtet und verknüpft – so erhalten Sie eine fundierte Gesamtbewertung und klare Handlungsempfehlungen.",
    color: "bg-[#7e3af2]",
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor" aria-hidden>
        <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 4l3 5-3 2-3-2 3-5zm-6 9l5-2v4l-5-2zm12 0l-5 2v-4l5 2zm-6 3l3-2 1 5h-8l1-5 3 2z" />
      </svg>
    ),
  },
];

type Vorteil = { title: string; sub: string; icon: React.ReactNode };

const vorteile: Vorteil[] = [
  {
    title: "Klarheit erhalten",
    sub: "Alle relevanten Faktoren transparent verstehen",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 4a6 6 0 110 12 6 6 0 010-12zm0 3a3 3 0 100 6 3 3 0 000-6z" />
      </svg>
    ),
  },
  {
    title: "Förderpotenziale erkennen",
    sub: "Maximale Förderungen sicher nutzen",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M14 4a8 8 0 100 16 8 8 0 005.7-2.3l-1.4-1.4A6 6 0 1114 6c1.3 0 2.5.4 3.5 1.2L14 11h7V4l-2 2A8 8 0 0014 4z" />
      </svg>
    ),
  },
  {
    title: "Kosten realistisch bewerten",
    sub: "Investitionen, Betriebskosten und Einsparungen im Blick",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M7 2h10a2 2 0 012 2v16a2 2 0 01-2 2H7a2 2 0 01-2-2V4a2 2 0 012-2zm0 4v3h10V6H7zm0 5v2h3v-2H7zm5 0v2h2v-2h-2zm-5 4v2h3v-2H7zm5 0v2h2v-2h-2z" />
      </svg>
    ),
  },
  {
    title: "Fehler vermeiden",
    sub: "Risiken frühzeitig erkennen und gezielt steuern",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M12 2L4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3zm-1.2 14.2l-3.5-3.5 1.4-1.4 2.1 2.1 5-5 1.4 1.4-6.4 6.4z" />
      </svg>
    ),
  },
  {
    title: "Besser entscheiden",
    sub: "Fundierte Empfehlungen für die beste Lösung",
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
        <path d="M9 16.2l-3.5-3.5L4 14.2 9 19.2 20 8.2l-1.4-1.4L9 16.2z" />
      </svg>
    ),
  },
];

function Page() {
  return (
    <div className="min-h-screen bg-[#f4f6fa] text-navy">
      <SiteNav />
      <main>
        {/* HERO */}
        <section className="border-b border-[var(--rule)]">
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-10 md:py-16 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h1 className="font-display font-bold text-navy text-3xl md:text-5xl leading-[1.05]">
                Was analysiert<br />
                Energy Prime <span className="text-orange">360</span>?
              </h1>
              <p className="mt-5 text-navy/80 text-base md:text-lg leading-relaxed max-w-[520px]">
                Energy Prime 360 analysiert Ihr Projekt ganzheitlich – technisch, wirtschaftlich und
                strategisch.
              </p>
              <p className="mt-3 text-navy/80 text-base md:text-lg leading-relaxed max-w-[520px]">
                Wir betrachten alle relevanten Faktoren in einem System, bewerten Chancen und Risiken
                und zeigen Ihnen klare Empfehlungen für die beste Entscheidung.
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <Check>Technik &amp; Energie</Check>
                <Check>Förderung &amp; Steuern</Check>
                <Check>Risiken &amp; Potenziale</Check>
              </div>
            </div>
            <div>
              <img
                src={analyseHero.url}
                alt="Energy Prime 360 Analyse-Dashboard mit Vergleichen, Förderungen, Gebäudefaktoren und Zukunftsbewertung"
                width={1280}
                height={960}
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </section>

        {/* FEATURE CARDS */}
        <section className="border-b border-[var(--rule)]">
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-12 md:py-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {features.map((f) => (
                <article
                  key={f.title}
                  className="bg-white rounded-2xl border border-[var(--rule)] shadow-sm p-6 flex flex-col items-center text-center hover:shadow-md transition"
                >
                  <span className={`w-14 h-14 rounded-full ${f.color} inline-flex items-center justify-center mb-4 shadow-sm`}>
                    {f.icon}
                  </span>
                  <h3 className="font-display font-semibold text-navy text-base leading-snug mb-3 min-h-[3rem]">
                    {f.title}
                  </h3>
                  <p className="text-sm text-navy/70 leading-relaxed">{f.body}</p>
                  <span className="mt-4 text-[var(--leaf-deep)] text-xl" aria-hidden>→</span>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* VORTEILE STRIP */}
        <section className="border-b border-[var(--rule)]" style={{ backgroundColor: "#eef1f5" }}>
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-10 md:py-12">
            <h2 className="text-center font-display font-bold text-navy text-xl md:text-2xl mb-8">
              Ihre Vorteile auf einen Blick
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
              {vorteile.map((v) => (
                <div key={v.title} className="flex items-start gap-3 bg-white rounded-xl border border-[var(--rule)] p-4">
                  <span className="text-[var(--leaf-deep)] shrink-0 mt-0.5">{v.icon}</span>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-navy text-sm leading-tight">{v.title}</div>
                    <div className="text-xs text-navy/65 mt-1 leading-snug">{v.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 12 Analysebereiche – neu gestaltete Kartenübersicht */}
        <AnalysisModulesGrid />

        <FaqList
          items={[
            { q: "Wie lange dauert eine Analyse?", a: "Erstbewertung innerhalb weniger Tage, Vollanalyse 1–2 Wochen je nach Datenlage." },
            { q: "Welche Unterlagen brauche ich?", a: "Grundriss/Lageplan, letzte Energie­abrechnungen, vorhandene Angebote. Mehr ist hilfreich, nicht zwingend." },
            { q: "Werden alle Bereiche immer geprüft?", a: "Standard ist die Gesamt­betrachtung. Einzel­module sind möglich, wir empfehlen sie aber nicht." },
          ]}
        />

        {/* CTA */}
        <section>
          <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-12 md:py-16 grid md:grid-cols-[1.1fr_1fr] gap-10 items-center">
            <div>
              <h2 className="font-display font-bold text-navy text-2xl md:text-3xl leading-tight">
                Bereit für Ihre fundierte <span className="text-[var(--leaf-deep)]">Analyse</span>?
              </h2>
              <p className="mt-4 text-navy/80 text-base leading-relaxed max-w-[540px]">
                Starten Sie jetzt Ihre ganzheitliche Projektanalyse und erhalten Sie klare Empfehlungen –
                kostenlos und unverbindlich.
              </p>
              <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3">
                <Check>In nur 2 Minuten Projekt starten</Check>
                <Check>100 % kostenlos</Check>
                <Check>100 % unverbindlich</Check>
              </div>
              <Link
                to="/projekt-beschreiben"
                className="mt-7 inline-flex items-center gap-2 bg-orange hover:bg-orange/90 text-white font-semibold px-6 py-3 rounded-md transition shadow-md"
              >
                Kostenlose Analyse starten
                <span aria-hidden>→</span>
              </Link>
            </div>
            <CtaIllustration />
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function CtaIllustration() {
  const items = [
    { label: "Technik & Energie", d: "M19.43 12.98a7.97 7.97 0 000-1.96l2.04-1.58a.5.5 0 00.12-.64l-1.93-3.34a.5.5 0 00-.61-.22l-2.4.96a8 8 0 00-1.7-.98l-.36-2.55A.5.5 0 0014.1 2h-3.86a.5.5 0 00-.5.42l-.36 2.55a8 8 0 00-1.7.98l-2.4-.96a.5.5 0 00-.61.22L2.74 8.55a.5.5 0 00.12.64l2.04 1.58a8 8 0 000 2.46l-2.04 1.58a.5.5 0 00-.12.64l1.93 3.34a.5.5 0 00.61.22l2.4-.96a8 8 0 001.7.98l.36 2.55a.5.5 0 00.5.42h3.86a.5.5 0 00.5-.42l.36-2.55a8 8 0 001.7-.98l2.4.96a.5.5 0 00.61-.22l1.93-3.34a.5.5 0 00-.12-.64l-2.04-1.58zM12 16a4 4 0 110-8 4 4 0 010 8z" },
    { label: "Förderung & Steuern", d: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 7V3.5L18.5 9H13z" },
    { label: "Finanzierung", d: "M4 21V9l8-6 8 6v12h-5v-6h-6v6H4z" },
    { label: "Risiken & Potenziale", d: "M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v5h2v-5z" },
    { label: "Wirtschaftlichkeit", d: "M14 4a8 8 0 100 16 8 8 0 005.7-2.3l-1.4-1.4A6 6 0 1114 6c1.3 0 2.5.4 3.5 1.2L14 11h7V4l-2 2A8 8 0 0014 4z" },
    { label: "Zukunftsperspektiven", d: "M17 8C8 10 5 16 4 22h2c.7-3 2-5 4-7v6h2v-6c4-1 6-4 5-7zm-2-5C9 4 5 8 7 14c3-3 6-4 8-11z" },
  ];
  return (
    <div className="relative w-full max-w-md mx-auto">
      <div className="relative aspect-square">
        <div className="absolute inset-1/2 -translate-x-1/2 -translate-y-1/2 w-44 h-44 bg-white rounded-2xl border border-[var(--rule)] shadow-lg flex items-center justify-center">
          <div className="text-center px-3">
            <span className="inline-flex w-12 h-12 rounded-full bg-[color-mix(in_oklab,var(--leaf)_15%,white)] items-center justify-center mb-2">
              <svg viewBox="0 0 24 24" className="w-6 h-6 text-[var(--leaf-deep)]" fill="currentColor" aria-hidden>
                <path d="M5 21V11h3v10H5zm5 0V3h3v18h-3zm5 0v-7h3v7h-3z" />
              </svg>
            </span>
            <div className="text-sm font-display font-bold text-navy leading-tight">Projektanalyse</div>
            <div className="text-[10px] text-navy/60 mt-1">Ganzheitlich · Transparent · Entscheidungsstark</div>
          </div>
        </div>
        {items.map((it, i) => {
          const angle = (i / items.length) * Math.PI * 2 - Math.PI / 2;
          const r = 44;
          const x = 50 + Math.cos(angle) * r;
          const y = 50 + Math.sin(angle) * r;
          return (
            <div
              key={it.label}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <span className="w-11 h-11 rounded-full bg-white border border-[var(--rule)] shadow-sm flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-[var(--leaf-deep)]" fill="currentColor" aria-hidden>
                  <path d={it.d} />
                </svg>
              </span>
              <span className="text-[10px] md:text-xs text-navy/70 font-medium text-center max-w-[90px]">{it.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
